// Client layer (TS-2.5.03) - UC-7/UC-8. Composes the service hook (useRoadmap) and the roadmap's
// presentation components; contains no business logic itself. "Show area" is single-select and
// narrows both the board (whole swimlanes hidden) and the detail sections; "Filter status" is
// multi-select additive and narrows detail sections while only dimming board chips - both axes
// round-trip as FilterChoice CODES to GET /api/roadmap (decisions.md D-199), and the server (not
// this component) decides which quarter columns exist (decisions.md D-147).
//
// GROUP-BY TOGGLE (decisions.md D-278): Team is this same server-derived board rendered as-is;
// Product is the SAME board's chips regrouped client-side by ProductGroupedBoard, which owns the
// extra reads that mode alone needs. The toggle's selection persists per session
// (lib/groupModePreference.ts, D-259's sessionStorage precedent) and applies ONLY here - the
// filters, legend and stat cards stay grouping-independent, and the detail sections below the
// board stay team-grouped regardless of the toggle (a deliberate non-goal, not an oversight: they
// mirror the BRD's reference layout, and D-266 scoped Product OUT of the Report Model/detail
// composition for v1).
import { useMemo, useState } from "react";
import { DetailSections } from "./DetailSections";
import { FilterBar } from "./FilterBar";
import { GroupModeToggle } from "./GroupModeToggle";
import { useProjectProductJoin } from "./hooks/useProjectProductJoin";
import { useRoadmap } from "./hooks/useRoadmap";
import { buildLegend } from "./lib/legend";
import { readGroupModePreference, writeGroupModePreference, type RoadmapGroupMode } from "./lib/groupModePreference";
import { resolveProjectOwners } from "./lib/resolveProjectOwners";
import { Legend } from "./Legend";
import { ProductGroupedBoard } from "./ProductGroupedBoard";
import { RecentChanges } from "./RecentChanges";
import "./roadmap.css";
import { RoadmapBoard } from "./RoadmapBoard";
import { StatCards } from "./StatCards";
import { useReferenceData } from "../../hooks/useReferenceData";
import type { ProjectView } from "../../api/generated/contracts";
import type { RoadmapFilterParams } from "../../api/roadmap";
import { strings } from "../../strings";

function projectsByKey(rows: ReadonlyArray<{ rows: readonly ProjectView[] }>): ReadonlyMap<string, ProjectView> {
  const map = new Map<string, ProjectView>();
  for (const group of rows) {
    for (const project of group.rows) {
      map.set(project.key, project);
    }
  }
  return map;
}

export function RoadmapPage() {
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<readonly string[]>([]);
  const [groupMode, setGroupMode] = useState<RoadmapGroupMode>(() => readGroupModePreference() ?? "team");

  function handleGroupModeChange(mode: RoadmapGroupMode) {
    setGroupMode(mode);
    writeGroupModePreference(mode);
  }

  const filter = useMemo<RoadmapFilterParams>(
    () => ({
      teamGroups: selectedArea ? [selectedArea] : undefined,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    }),
    [selectedArea, selectedStatuses],
  );

  const { options } = useReferenceData();
  const { view, loading, error } = useRoadmap(filter);

  // D-280 (revising D-278's Product-mode-only gating): the detail cards below now resolve a
  // product name and an effective business owner (D-279) on EVERY card, in BOTH group modes - not
  // only when the Team/Product toggle is set to Product - so this single read replaces the one
  // ProductGroupedBoard used to fetch for itself, fetched once here regardless of mode and handed
  // down to both consumers rather than doubled.
  const projectProductJoin = useProjectProductJoin();
  const ownerInfoByProjectKey = useMemo(
    () =>
      projectProductJoin.projects && projectProductJoin.products
        ? resolveProjectOwners(projectProductJoin.projects, projectProductJoin.products)
        : undefined,
    [projectProductJoin.projects, projectProductJoin.products],
  );

  if (loading && !view) {
    return (
      <section>
        <h1>Roadmap</h1>
        <div role="status" aria-live="polite">
          {strings.roadmap.loading}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <h1>Roadmap</h1>
        <div role="alert" className="eg-roadmap-error">
          <p>{strings.roadmap.errorTitle}</p>
          <p>{error.message}</p>
          {error.kind === "serverError" && <p>{strings.roadmap.firstDayHint}</p>}
        </div>
      </section>
    );
  }

  if (!view) {
    return (
      <section>
        <h1>Roadmap</h1>
      </section>
    );
  }

  const legendEntries = buildLegend(view.board, options?.statuses ?? []);
  const byKey = projectsByKey(view.cards);
  const isEmpty = view.board.swimlanes.every((lane) => lane.chips.length === 0);

  return (
    <section className="eg-roadmap">
      <header className="eg-roadmap__header">
        <h1>Roadmap</h1>
        <p className="eg-roadmap__as-of">As of {new Date(view.header.dataAsOfUtc).toLocaleString()}</p>
      </header>

      <StatCards cards={view.counts.cards} projectsByKey={byKey} />

      {options && (
        <FilterBar
          areas={options.areas}
          statuses={options.statuses}
          selectedArea={selectedArea}
          selectedStatuses={selectedStatuses}
          onAreaChange={setSelectedArea}
          onStatusesChange={setSelectedStatuses}
        />
      )}

      <GroupModeToggle mode={groupMode} onChange={handleGroupModeChange} />

      {isEmpty ? (
        <p className="eg-board-empty-state">{strings.roadmap.emptyBoard}</p>
      ) : groupMode === "product" ? (
        <ProductGroupedBoard
          board={view.board}
          statuses={options?.statuses ?? []}
          selectedStatuses={selectedStatuses}
          dimmedOpacity={view.filterSemantics.dimmedOpacity}
          emptyCellPlaceholder={view.profile.emptyCellPlaceholder}
          projectsByKey={byKey}
          projects={projectProductJoin.projects}
          products={projectProductJoin.products}
          loading={projectProductJoin.loading}
          error={projectProductJoin.error}
        />
      ) : (
        <RoadmapBoard
          board={view.board}
          statuses={options?.statuses ?? []}
          selectedStatuses={selectedStatuses}
          dimmedOpacity={view.filterSemantics.dimmedOpacity}
          emptyCellPlaceholder={view.profile.emptyCellPlaceholder}
          projectsByKey={byKey}
        />
      )}

      <Legend entries={legendEntries} />

      <DetailSections
        groups={view.cards}
        hideEmptyGroups={view.filterSemantics.hideGroupWhenNoVisibleCards}
        ownerInfoByProjectKey={ownerInfoByProjectKey}
      />

      {/* BELOW the detail sections and OUTSIDE the filter, deliberately. The board and the cards
          answer "what is the state of the portfolio under this filter"; this answers "what has
          moved lately", which is a question about the whole portfolio - narrowing it by the
          reader's current filter selection would hide the change that made them look. It fetches
          its own data (useRecentChanges) rather than riding RoadmapView, because a card refreshed
          by the push channel must not make a filter change cost a second board composition. */}
      <RecentChanges />
    </section>
  );
}
